import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Filter,
  Calendar,
  Search,
  Download,
  RefreshCw,
  MessageCircle,
  Send,
  Smartphone
} from 'lucide-react';
import { whatsappService } from '../../../../services/whatsappService';


const DeliveryTracking = () => {
  const [deliveryReports, setDeliveryReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [filters, setFilters] = useState({
    status: 'all',
    date_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0], // Last 7 days
    date_to: new Date()?.toISOString()?.split('T')?.[0],
    search: ''
  });

  const statusTypes = [
    { value: 'all', label: 'All Status', icon: Eye, color: 'text-gray-400' },
    { value: 'pending', label: 'Pending', icon: Clock, color: 'text-yellow-400' },
    { value: 'sent', label: 'Sent', icon: Send, color: 'text-blue-400' },
    { value: 'delivered', label: 'Delivered', icon: CheckCircle, color: 'text-green-400' },
    { value: 'read', label: 'Read', icon: Eye, color: 'text-purple-400' },
    { value: 'failed', label: 'Failed', icon: AlertCircle, color: 'text-red-400' }
  ];

  useEffect(() => {
    loadInitialData();
    setupRealtimeSubscription();
  }, []);

  useEffect(() => {
    loadFilteredData();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadDeliveryReports(),
        loadAnalytics()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryReports = async () => {
    try {
      const { data } = await whatsappService?.getDeliveryReports();
      setDeliveryReports(data || []);
    } catch (error) {
      console.error('Error loading delivery reports:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const { data } = await whatsappService?.getAnalytics({
        from: filters?.date_from,
        to: filters?.date_to
      });
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadFilteredData = async () => {
    try {
      // Apply filters to delivery reports
      let filteredReports = deliveryReports;

      if (filters?.status !== 'all') {
        filteredReports = filteredReports?.filter(report => report?.status === filters?.status);
      }

      if (filters?.search) {
        filteredReports = filteredReports?.filter(report =>
          report?.message?.recipient?.full_name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
          report?.message?.phone_number?.includes(filters?.search)
        );
      }

      setDeliveryReports(filteredReports);
      await loadAnalytics();
    } catch (error) {
      console.error('Error applying filters:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    const subscription = whatsappService?.subscribeToDeliveryReports((payload) => {
      if (payload?.eventType === 'INSERT') {
        setDeliveryReports(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setDeliveryReports(prev => prev?.map(report =>
          report?.id === payload?.new?.id ? payload?.new : report
        ));
      }
    });

    return () => subscription?.unsubscribe();
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await loadInitialData();
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusIcon = (status) => {
    const statusType = statusTypes?.find(t => t?.value === status);
    return statusType ? statusType?.icon : Clock;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'sent': return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      case 'delivered': return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'read': return 'text-purple-400 bg-purple-500/20 border-purple-500/30';
      case 'failed': return 'text-red-400 bg-red-500/20 border-red-500/30';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    }
  };

  const calculateDeliveryRate = () => {
    if (!analytics || analytics?.total_messages === 0) return 0;
    return Math.round((analytics?.delivered / analytics?.total_messages) * 100);
  };

  const calculateReadRate = () => {
    if (!analytics || analytics?.delivered === 0) return 0;
    return Math.round((analytics?.read / analytics?.delivered) * 100);
  };

  const getDeliveryTrend = () => {
    // Calculate trend based on recent data (simplified)
    const rate = calculateDeliveryRate();
    if (rate >= 90) return { direction: 'up', color: 'text-green-400', value: '+5%' };
    if (rate >= 70) return { direction: 'neutral', color: 'text-yellow-400', value: '±0%' };
    return { direction: 'down', color: 'text-red-400', value: '-3%' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
      </div>
    );
  }

  const deliveryTrend = getDeliveryTrend();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400">
            <Eye className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Delivery Tracking</h2>
            <p className="text-gray-400">Monitor WhatsApp message delivery and read receipts</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="px-4 py-2 text-gray-400 hover:text-white border border-gray-600 hover:border-gray-500 rounded-xl transition-colors flex items-center space-x-2"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <button
            onClick={() => {/* Export functionality */ }}
            className="px-4 py-2 text-gray-400 hover:text-white border border-gray-600 hover:border-gray-500 rounded-xl transition-colors flex items-center space-x-2"
          >
            <Download className="w-5 h-5" />
            <span>Export</span>
          </button>
        </div>
      </div>
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400">
              <MessageCircle className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Total Messages</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {analytics?.total_messages || 0}
          </div>
          <div className="text-sm text-gray-400">Last 7 days</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-green-500/20 text-green-400">
              <CheckCircle className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Delivery Rate</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {calculateDeliveryRate()}%
          </div>
          <div className={`text-sm flex items-center space-x-1 ${deliveryTrend?.color}`}>
            <TrendingUp className="w-4 h-4" />
            <span>{deliveryTrend?.value} from last week</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400">
              <Eye className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Read Rate</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {calculateReadRate()}%
          </div>
          <div className="text-sm text-purple-400">
            {analytics?.read || 0} messages read
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-red-500/20 text-red-400">
              <AlertCircle className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Failed Messages</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {analytics?.failed || 0}
          </div>
          <div className="text-sm text-red-400">
            {analytics?.total_messages ? ((analytics?.failed / analytics?.total_messages) * 100)?.toFixed(1) : 0}% failure rate
          </div>
        </motion.div>
      </div>
      {/* Status Filter Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
      >
        <div className="flex flex-wrap gap-2 mb-6">
          {statusTypes?.map((status) => {
            const Icon = status?.icon;
            const count = status?.value === 'all'
              ? deliveryReports?.length
              : deliveryReports?.filter(r => r?.status === status?.value)?.length;

            return (
              <button
                key={status?.value}
                onClick={() => setFilters(prev => ({ ...prev, status: status?.value }))}
                className={`px-4 py-2 rounded-xl border-2 transition-all flex items-center space-x-2 ${filters?.status === status?.value
                  ? 'border-blue-500 bg-blue-500/20 text-blue-400' : 'border-white/10 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white'
                  }`}
              >
                <Icon className="w-4 h-4" />
                <span>{status?.label}</span>
                <span className="bg-white/20 px-2 py-1 rounded-full text-xs">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Search and Date Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={filters?.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e?.target?.value }))}
              className="pl-10 pr-4 py-3 w-full bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="date"
              value={filters?.date_from}
              onChange={(e) => setFilters(prev => ({ ...prev, date_from: e?.target?.value }))}
              className="pl-10 pr-4 py-3 w-full bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="date"
              value={filters?.date_to}
              onChange={(e) => setFilters(prev => ({ ...prev, date_to: e?.target?.value }))}
              className="pl-10 pr-4 py-3 w-full bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          <button
            onClick={() => setFilters({
              status: 'all',
              date_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
              date_to: new Date()?.toISOString()?.split('T')?.[0],
              search: ''
            })}
            className="px-4 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-xl transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </motion.div>
      {/* Delivery Reports Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden"
      >
        <div className="p-6 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">Delivery Reports</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Recipient</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Phone</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Sent Time</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Delivered Time</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Error Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {deliveryReports?.length > 0 ? (
                deliveryReports?.map((report) => {
                  const StatusIcon = getStatusIcon(report?.status);
                  return (
                    <tr key={report?.id} className="hover:bg-white/5">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                            {report?.message?.recipient?.full_name?.charAt(0) || 'U'}
                          </div>
                          <span className="text-white font-medium">
                            {report?.message?.recipient?.full_name || 'Unknown User'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        <div className="flex items-center space-x-2">
                          <Smartphone className="w-4 h-4 text-gray-500" />
                          <span>{report?.message?.phone_number}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(report?.status)}`}>
                          <StatusIcon className="w-3 h-3" />
                          <span>{report?.status?.toUpperCase()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {new Date(report.timestamp)?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {report?.status === 'delivered' || report?.status === 'read'
                          ? new Date(report.timestamp)?.toLocaleString()
                          : '—'
                        }
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {report?.error_description ? (
                          <div className="text-red-400 max-w-xs truncate" title={report?.error_description}>
                            {report?.error_description}
                          </div>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Eye className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-300 mb-2">No delivery reports</h3>
                    <p className="text-gray-400">Delivery reports will appear here once messages are sent</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default DeliveryTracking;